import { ActivitySpinnerSize,ActivitySpinnerWidget, FilteringList, ListItem, TabStyles } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { DeprecatedInterpolatedString } from '~commonInfra/components/InterpolatedString';
import { ReactActionDialogBodyProps } from '~commonInfra/dialogs/ReactActionDialog';
import { HelpService } from '~commonInfra/services/HelpService';

import { Messages } from '../../../codegen/Localize';
import { ResourceQuery } from '../../../httpClient/resources/ResourceQuery';
import { cssClass } from '../../../style';
import { IUserName } from '../../../userManagement/users/serverApi/types/IUser';

export const INITIAL_SLICE_SIZE = 20;
export const SLICE_SIZE = 1000;

export type SetOwnerDialogBodyProps = {
  readonly prompt: string;
  readonly showCredentialsWarning: boolean;
  readonly isAdmin: boolean;
  readonly isOwner: boolean;
  readonly getQuery: (matchText: string) => ResourceQuery<IUserName>;
};
export type SetOwnerDialogState = {
  readonly targetOwner: IUserName;
};
export type PropsForConfirmActionDialogBody = ReactActionDialogBodyProps<SetOwnerDialogBodyProps, SetOwnerDialogState>;

export function SetOwnerDialogBody(props: PropsForConfirmActionDialogBody): JSX.Element {
  const [searchText, setSearchText] = React.useState('');
  const [items, setItems] = React.useState([]);
  const [showSpinner, setShowSpinner] = React.useState(true);
  const [isError, setIsError] = React.useState(false);

  let ownerAccessWarning = '';
  if (props.isAdmin) {
    ownerAccessWarning = Messages.changeOwnerAdminWarning();
  } else if (props.isOwner) {
    ownerAccessWarning = Messages.changeOwnerCurrentOwnerWarning();
  }

  const onSelectUser = React.useCallback(
    (value: string) => {
      const item = items.find((item) => item.value === value);
      if (item) {
        props.updateDialogState({ targetOwner: item.user });
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.updateDialogState, items]
  );

  const onUserPickerFilterChange = React.useCallback(
    (value: string) => {
      setSearchText(value);
    },
    []
  );

  // We first fetch an initial 20 users so that we can quickly render something to the user
  // and immediately fetch all users after that.  We do this in 1000 slice sizes meaning
  // we continue fetching users until we've fetched all of them.
  // If user types in a search, the currently running results are ignored using the Effect hook cleanup function,
  // and triggers another round of fetching all users; first by initial slice, and then all of them
  React.useEffect(
    () => {
      let ignore = false;
      async function fetchUsers(start: number, size: number) {
        if (size === INITIAL_SLICE_SIZE) {
          setIsError(false);
        }
        try {
          const usersResult = await props.getQuery(searchText).getSlice(start, size);
          const newItems: ListItem[] = usersResult.items.map((user) => ({
            content: createItemElement(user.displayName),
            value: '' + user.id,
            testId: 'list-item-' + user.id,
            user,
          }));
          if (!ignore) {
            if (start === 0) {
              setShowSpinner(false);
              setItems(newItems);
            } else {
              // ensure we are setting items with latest items state
              setItems((items) => items.concat(newItems));
            }
          }

          if (size !== INITIAL_SLICE_SIZE && usersResult.total > start + newItems.length) {
            fetchUsers(start+newItems.length, SLICE_SIZE);
          }
        } catch (e) {
          setShowSpinner(false);
          setIsError(true);
        }
      }

      fetchUsers(0, INITIAL_SLICE_SIZE);
      fetchUsers(0, SLICE_SIZE);
      return () => {ignore = true;};
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchText]
  );

  /* eslint-disable jsx-a11y/no-autofocus */
  return (
    <>
      <div className={prompt}>
        {props.prompt}
      </div>
      {ownerAccessWarning &&
        <div className={warning} data-tb-test-id='set-owner-access-warning'>
          {ownerAccessWarning}
        </div>
      }
      {props.showCredentialsWarning && <CredentialsWarning />}
      <div style={{ height: 300 }}>
        <FilteringList
          items={items}
          onSelect={onSelectUser}
          searchPlaceholder={Messages.search()}
          testId="filtering-list-set-owner"
          style={{height: '100%', maxHeight:'300px', width: 'auto', border: '1px solid #b2b2b2'}}
          onFilterChange={onUserPickerFilterChange}
          searchText={searchText}
          autoFocus={true}
          inputTestId="owner-picker-filter"
          listTestId="owner-picker-list"
        >
          {!showSpinner && (items.length === 0 || isError) &&
            <div className={noResults} data-tb-test-id="set-owner-no-results">
              {hasSearchText(searchText)? Messages.noMatchingResults() : Messages.noUsersAvailable()}
            </div>
          }
          {showSpinner &&
            <div className={spinner}>
              <ActivitySpinnerWidget
                spinnerSize={ActivitySpinnerSize.Large}
                testId={'owner-picker'}
              />
            </div>
          }
        </FilteringList>
      </div>
    </>
  );
}

const createItemElement = (userDisplayname: string) =>
  (<span style={{fontSize: '12px', lineHeight: '27px'}}>
    {userDisplayname}
  </span>);

const CredentialsWarning = React.memo((): JSX.Element => {
  const url = HelpService.getServerHelpUrlByCategory('contentowner');
  const link = () => <a href={url}>{Messages.changeOwnerPromptLearnMore()}</a>;
  return (
    <div className={prompt} data-tb-test-id='set-owner-credentials-warning'>
      <DeprecatedInterpolatedString
        baseStringKey="changeOwnerWorkbookPromptDescription"
        translate={() => Messages.changeOwnerWorkbookPromptDescription({LEARN_MORE_CHANGE_OWNER: '{LEARN_MORE_CHANGE_OWNER}'})}
        replacements={{ LEARN_MORE_CHANGE_OWNER: link }}
      />
    </div>
  );
});

function hasSearchText(text: string): boolean {
  return !_.isUndefined(text) && text.length !== 0;
}

const prompt = cssClass('set-owner-dialog', 'prompt', {
  fontSize: TabStyles.Typography.Sizing.Large,
  marginBottom: TabStyles.Spacing.LargeSpace,
  wordWrap: 'break-word'
});

const warning = cssClass('set-owner-dialog', 'warning', {
  fontSize: TabStyles.Typography.Sizing.Large,
  marginBottom: TabStyles.Spacing.LargeSpace,
  wordWrap: 'break-word'
});

const spinner = cssClass('set-owner-dialog', 'spinner', {
  marginTop: 18,
  textAlign: 'center'
});

const noResults = cssClass('set-owner-dialog', 'no-results', {
  fontSize: '15px',
  marginTop: 18,
  padding: 6,
  lineHeight: '23px',
  textAlign: 'center',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});
